// SceltaAvatar.tsx — «Scegli la tua faccia» (v019): tocchi un avatar, lo vedi grande come nella Home, «Fatto!».
// Si salva sul telefono e, per chi ha l'account, sul server (servizi/account.ts → scegliAvatar).
import { tastiRadio } from "../componenti/tastiRadio.ts";
import { useEffect, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { Avatar } from "../componenti/Avatar.tsx";
import { salvaAspettoESegui, useAvatar } from "../servizi/account.ts";
import { ErroreRete } from "../servizi/api.ts";
import { segnalaErrore } from "../servizi/errori.ts";
import { COLORI_PRIMARI, applicaColore, coloreSalvato, type IdColore } from "../temi/colori.ts";
import { AVATAR_NUMERO } from "../../../condiviso/avatar.ts";

const NUMERI = Array.from({ length: AVATAR_NUMERO }, (_, i) => i + 1);

export function SceltaAvatar({ onIndietro }: { onIndietro: () => void }) {
  const { t } = useTranslation();
  const attuale = useAvatar();
  const [scelto, setScelto] = useState(attuale);
  const [invio, setInvio] = useState(false);
  const [colore, setColore] = useState<IdColore>(coloreSalvato());
  // uscendo senza «Fatto!» si torna al colore di prima (l'anteprima non resta)
  useEffect(() => () => applicaColore(), []);

  // v038: «Fatto!» torna sempre al profilo (il proprietario); il conto si aggiorna in sottofondo
  const fatto = () => {
    setInvio(true);
    void salvaAspettoESegui(scelto, colore, (e) => {
      const codice = e instanceof ErroreRete ? e.codice : "server";
      const stato = e instanceof ErroreRete ? e.stato : 0;
      if (!["offline", "timeout"].includes(codice)) segnalaErrore(new Error(`avatar non salvato sul conto: ${codice} (HTTP ${stato})`));
    }).then(onIndietro);
  };

  return (
    <div className="pagina ld-pagina ld-avatar-pagina">
      <header className="ld-barra">
        <button type="button" className="ld-indietro" aria-label={t("account.indietro")} onClick={onIndietro}>
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M15 5l-7 7 7 7" />
          </svg>
        </button>
      </header>
      <div className="ld-avatar-anteprima">
        <div className="ld-alone ld-alone-piccolo" aria-hidden="true" />
        <Avatar key={scelto} quale={scelto} dimensione={200} alt={t("avatar.tuo")} classe="ld-avatar-grande" />
      </div>
      <h1 className="ld-titolo ld-centrato">
        {t("avatar.titolo_1")} <span className="ld-giallo">{t("avatar.titolo_2")}</span>
      </h1>
      <p className="ld-sottotitolo ld-centrato">{t("avatar.testo")}</p>
      {/* v021: il colore primario (pulsanti, titoli, icone e il cerchio dietro l'avatar), al posto dei temi */}
      <p className="ld-colori-titolo" id="titolo-colori">
        {t("avatar.colore_titolo")}
      </p>
      <div className="ld-colori" role="radiogroup" aria-labelledby="titolo-colori" onKeyDown={tastiRadio}>
        {COLORI_PRIMARI.map((c) => (
          <button
            key={c.id}
            type="button"
            role="radio"
            aria-checked={c.id === colore}
            tabIndex={c.id === colore ? 0 : -1}
            aria-label={t(`colore.${c.id}`)}
            title={t(`colore.${c.id}`)}
            className="ld-colore-voce"
            style={{ "--ld-col": c.hex } as CSSProperties}
            onClick={() => {
              setColore(c.id);
              applicaColore(c.id); // anteprima subito
            }}
          />
        ))}
      </div>
      <div className="ld-avatar-griglia" role="radiogroup" aria-label={t("avatar.titolo_1") + " " + t("avatar.titolo_2")} onKeyDown={tastiRadio}>
        {NUMERI.map((n) => (
          <button key={n} type="button" role="radio" aria-checked={n === scelto} tabIndex={n === scelto ? 0 : -1} aria-label={t("avatar.numero", { n })} className="ld-avatar-voce" onClick={() => setScelto(n)}>
            <Avatar quale={n} dimensione={72} />
          </button>
        ))}
      </div>
      <div className="ld-fondo-fisso">
        <button type="button" className="ld-cta ld-cta-centrata" disabled={invio} onClick={fatto}>
          {t("avatar.fatto")}
        </button>
      </div>
    </div>
  );
}
